import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styles from './VolunteerManagement.module.css';
import MeetPageHeader from 'components/NewDesign/MeetPageHeader';
import { Users } from 'lucide-react';
import CustomConfirmModal from 'components/common/CustomConfirmModal';
import routes from 'routes';
import { convertDateTime } from 'utils/time';
import {
  getAllMeetSignups,
  manageSignups,
  bulkSignup,
  getClubMembers,
} from 'api/volunteerApi';
import Loading from 'components/common/Loading';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getUserPermissionLevel } from 'services/auth/tokenService';
import { UserPermissionLevel } from 'constants/auth';
import useGroupLabels from 'hooks/useGroupLabels';
import { groupOrderOfName, groupOrderOfRange } from 'utils/groupLabel';

const VolunteerManagement = ({ clubId, meetId }) => {
  // The group filter and the group headings read Age Group, or Year Group for a club that groups by year level.
  const { words } = useGroupLabels(clubId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clubMembers, setClubMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkSignup, setShowBulkSignup] = useState(false);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('All club roles');
  const [ageGroupFilter, setAgeGroupFilter] = useState('All age groups');
  const [hideFullyStaffed, setHideFullyStaffed] = useState(true);
  const navigate = useNavigate();
  const userPermissionLevel = getUserPermissionLevel();

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    content: '',
    confirmLabel: '',
    onConfirm: () => {},
    confirmClassName: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      if (userPermissionLevel < UserPermissionLevel.Admin) {
        return navigate(routes.admin.showMeet.url(clubId, meetId));
      }
      setLoading(true);
      try {
        const { data: meetData } = await getAllMeetSignups(meetId);
        setData(meetData);

        // If admin, also fetch club members
        if (userPermissionLevel >= UserPermissionLevel.Admin) {
          const { data: members } = await getClubMembers(clubId);
          setClubMembers(members);
        }
      } catch (err) {
        toast.error(err.message || 'Failed to load volunteer data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [meetId, clubId, userPermissionLevel, navigate]);

  // The role list is a single-selection <select>. While no role is chosen its
  // value ('') matches no option, so the browser leaves the FIRST option
  // selected: role #1 looks highlighted, and clicking it changes nothing in the
  // DOM, so no change event fires and onChange never runs. Force "no selection"
  // whenever no role is chosen, so the first click on any role registers.
  // Guarded on !selectedRole — once a role IS chosen, React's controlled value
  // owns the selection and we must not clear it.
  const roleSelectRef = useRef(null);

  useEffect(() => {
    if (roleSelectRef.current && !selectedRole) {
      roleSelectRef.current.selectedIndex = -1;
    }
  });

  const isFieldEvent = (roleName) => {
    const fieldEvents = [
      'High Jump',
      'Long Jump',
      'Triple Jump',
      'Discus',
      'Javelin',
      'Shot Put',
      'Floppy Jump',
    ];
    return fieldEvents.some((event) => roleName.includes(event));
  };

  // Updated grouping function to handle age groups properly
  const getGroupedAndSortedRoles = (roles) => {
    const general = [];
    const eventSpecific = [];
    const byAgeGroup = {};
    const byAthleteGroup = {};

    roles.forEach((role) => {
      if (role.roleCategory === 'General') {
        general.push(role);
      } else if (role.roleCategory === 'EventSpecific') {
        eventSpecific.push(role);
      } else if (role.roleCategory === 'AgeGroupSpecific') {
        // Use groupType to distinguish
        if (role.groupType === 'AthleteGroup' && role.programGroupName) {
          const key = role.programGroupName;
          if (!byAthleteGroup[key]) {
            byAthleteGroup[key] = {
              groupName: role.programGroupName,
              ageGroupCode: role.ageGroupCode,
              roles: [],
            };
          }
          byAthleteGroup[key].roles.push(role);
        } else if (role.groupType === 'AgeGroup' && role.programGroupName) {
          // Use programGroupName for display
          const key = role.programGroupName;
          if (!byAgeGroup[key]) {
            byAgeGroup[key] = {
              displayName: role.programGroupName,
              ageGroupCode: role.ageGroupCode,
              roles: [],
            };
          }
          byAgeGroup[key].roles.push(role);
        }
      }
    });

    // Sort event-specific roles
    eventSpecific.sort((a, b) => {
      const aIsField = isFieldEvent(a.roleName);
      const bIsField = isFieldEvent(b.roleName);
      if (aIsField && !bIsField) return 1;
      if (!aIsField && bIsField) return -1;
      return a.roleName.localeCompare(b.roleName);
    });

    // Sort roles within each group
    Object.values(byAgeGroup).forEach((group) => {
      group.roles.sort((a, b) => a.roleName.localeCompare(b.roleName));
    });

    Object.values(byAthleteGroup).forEach((group) => {
      group.roles.sort((a, b) => a.roleName.localeCompare(b.roleName));
    });

    return { general, eventSpecific, byAgeGroup, byAthleteGroup };
  };

  // Get unique age groups from roles
  const getUniqueAgeGroups = () => {
    if (!data || !data.roles) return ['All age groups'];

    const ageGroupCodeByName = new Map();
    data.roles.forEach((role) => {
      if (role.programGroupName && !ageGroupCodeByName.has(role.programGroupName)) {
        ageGroupCodeByName.set(role.programGroupName, role.ageGroupCode);
      }
    });

    const ageGroups = Array.from(ageGroupCodeByName.keys()).sort((a, b) => {
      // By the group's ages (its stored range), as the group sections below are,
      // so "Junior" (ages 8 to 10) comes with the 8s and not last
      const order =
        groupOrderOfRange(ageGroupCodeByName.get(a), a) -
        groupOrderOfRange(ageGroupCodeByName.get(b), b);
      // Same ages: by name, so "U8 F" comes before "U8 M"
      return order !== 0 ? order : a.localeCompare(b);
    });
    return ['All age groups', ...ageGroups];
  };

  // Filter roles based on age group selection and fully staffed status
  const getFilteredRoles = () => {
    if (!data || !data.roles) return [];

    const filtered = data.roles.filter((role) => {
      // Filter out fully staffed roles if checkbox is checked
      if (hideFullyStaffed && role.isFullyStaffed) {
        return false;
      }

      // Filter by age group
      if (ageGroupFilter === 'All age groups') {
        return true;
      }

      // Include General and EventSpecific roles in all age group filters
      if (
        role.roleCategory === 'General' ||
        role.roleCategory === 'EventSpecific'
      ) {
        return true;
      }

      // Filter age-specific roles by selected age group
      return role.programGroupName === ageGroupFilter;
    });

    // Sort roles by category first, then by age group
    filtered.sort((a, b) => {
      // Category order: General first, then EventSpecific, then AgeGroupSpecific
      const categoryOrder = { General: 0, EventSpecific: 1, AgeGroupSpecific: 2 };
      const aCatOrder = categoryOrder[a.roleCategory] ?? 3;
      const bCatOrder = categoryOrder[b.roleCategory] ?? 3;

      if (aCatOrder !== bCatOrder) {
        return aCatOrder - bCatOrder;
      }

      // For AgeGroupSpecific roles, sort by the group's ages, as the group filter is
      if (a.roleCategory === 'AgeGroupSpecific' && b.roleCategory === 'AgeGroupSpecific') {
        const aOrder = groupOrderOfRange(a.ageGroupCode, a.programGroupName);
        const bOrder = groupOrderOfRange(b.ageGroupCode, b.programGroupName);

        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }

        // Same age, sort alphabetically by programGroupName (e.g., "U8 F" before "U8 M")
        const aName = a.programGroupName || '';
        const bName = b.programGroupName || '';
        if (aName !== bName) {
          return aName.localeCompare(bName);
        }
      }

      // Finally, sort by role name
      return (a.roleName || '').localeCompare(b.roleName || '');
    });

    return filtered;
  };

  // Format role name with age group prefix for age-specific roles
  const formatRoleName = (role) => {
    if (role.programGroupName && role.roleCategory === 'AgeGroupSpecific') {
      return `${role.programGroupName}: ${role.roleName}`;
    }
    return role.roleName;
  };

  const getUserSignupCount = (userId) => {
    return data.roles.reduce((count, role) => {
      return (
        count + role.signups.filter((signup) => signup.userID === userId).length
      );
    }, 0);
  };

  //const getMemberDisplayName = (member) => {
  //  return `${member.firstName} ${member.lastName}`;
  //  };

  const getMemberDisplayName = (member) => {
    const first = (member.firstName || '').trim();
    const last = (member.lastName || '').trim();
    const full = `${first} ${last}`.trim();
    if (full) return full;
    return member.email || 'Unnamed';
  };

  const getMemberSortKey = (member) => {
    const first = (member.firstName || '').trim();
    const last = (member.lastName || '').trim();
    if (last || first) return `${last} ${first}`.trim();
    return member.email || 'Unnamed';
  };

  // Get unique roles from club members
  const getUniqueRoles = () => {
    const roles = new Set(['All club roles']);
    clubMembers.forEach((member) => {
      if (member.role) {
        roles.add(member.role);
      }
    });
    return Array.from(roles);
  };

  // Filter members based on search and role, sorted alphabetically
  const getFilteredMembers = () => {
    return clubMembers
      .filter((member) => {
        const nameMatch = getMemberDisplayName(member)
          .toLowerCase()
          .includes(memberSearchTerm.toLowerCase());
        const roleMatch =
          roleFilter === 'All club roles' || member.role === roleFilter;
        return nameMatch && roleMatch;
      })
      .sort((a, b) =>
        getMemberSortKey(a).localeCompare(getMemberSortKey(b))
      );
  };

  // Validation functions for bulk signup
  const getAvailableSlots = () => {
    if (!selectedRole) return 0;
    const role = data.roles.find(
      (r) => r.meetVolunteerRequirementID === selectedRole
    );
    return role ? role.numberStillNeeded : 0;
  };

  const getSelectionStatus = () => {
    if (!selectedRole) return null;

    const availableSlots = getAvailableSlots();
    const currentSelection = selectedMembers.length;

    if (availableSlots === 0)
      return { message: 'Role is fully staffed', type: 'warning' };
    if (currentSelection === 0)
      return {
        message: `${availableSlots} slot${availableSlots === 1 ? '' : 's'} to fill`,
        type: 'info',
      };
    if (currentSelection === availableSlots)
      return { message: 'All slots filled', type: 'success' };
    if (currentSelection > availableSlots)
      return {
        message: `Too many selected (${currentSelection}/${availableSlots})`,
        type: 'error',
      };
    return {
      message: `${currentSelection} of ${availableSlots} slots filled`,
      type: 'info',
    };
  };

  // Handle adding a member to selected list
  const handleAddMember = (memberId) => {
    const member = clubMembers.find((m) => m.userID === memberId);
    if (!member) return;

    // Check if already selected
    if (selectedMembers.some((m) => m.userID === memberId)) {
      toast.info(`${getMemberDisplayName(member)} is already selected`);
      return;
    }

    // Check if member is already signed up for this role
    if (selectedRole) {
      const role = data.roles.find(
        (r) => r.meetVolunteerRequirementID === selectedRole
      );
      if (role?.signups.some((s) => s.userID === memberId)) {
        toast.warning(
          `${getMemberDisplayName(member)} is already signed up for this role`
        );
        return;
      }
    }

    // Check available slots
    const availableSlots = getAvailableSlots();
    if (selectedRole && selectedMembers.length >= availableSlots) {
      toast.warning(
        `Only ${availableSlots} volunteer${availableSlots === 1 ? '' : 's'} needed for this role`
      );
      return;
    }

    setSelectedMembers([...selectedMembers, member]);
    setMemberSearchTerm(''); // Clear search after selection
  };

  // Handle removing a member from selected list
  const handleRemoveMember = (memberId) => {
    setSelectedMembers(selectedMembers.filter((m) => m.userID !== memberId));
  };

  // Clear all selected members
  const handleClearAll = () => {
    setSelectedMembers([]);
  };

  // Confirmation helper functions
  const getConfirmationDetails = (
    currentStatus,
    newAction,
    memberName,
    roleName
  ) => {
    const confirmations = {
      complete: {
        title: 'Mark as Complete',
        content: (
          <div>
            <p>
              Mark <strong>{memberName}</strong>&apos;s assignment for{' '}
              <strong>{roleName}</strong> as complete?
            </p>
            <div
              style={{
                background: '#e6f3ff',
                padding: '12px',
                borderRadius: '6px',
                marginTop: '12px',
              }}
            >
              <strong>✅ This will:</strong>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Mark the signup as completed</li>
                <li>Award volunteer points to the member</li>
              </ul>
            </div>
          </div>
        ),
        confirmLabel: 'Mark Complete',
        confirmClassName: styles.confirmComplete,
      },
      no_show: {
        title: 'Mark as No-Show',
        content: (
          <div>
            <p>
              Mark <strong>{memberName}</strong> as no-show for{' '}
              <strong>{roleName}</strong>?
            </p>
            <div
              style={{
                background: '#fff3cd',
                padding: '12px',
                borderRadius: '6px',
                marginTop: '12px',
              }}
            >
              <strong>⚠️ This will:</strong>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Mark the signup as no-show</li>
                <li>No volunteer points will be awarded</li>
              </ul>
            </div>
          </div>
        ),
        confirmLabel: 'Mark No-Show',
        confirmClassName: styles.confirmNoShow,
      },
      registered: {
        title: 'Mark as Incomplete',
        content: (
          <div>
            <p>
              Reset <strong>{memberName}</strong>&apos;s status for{' '}
              <strong>{roleName}</strong> back to registered?
            </p>
            <div
              style={{
                background: '#f8d7da',
                padding: '12px',
                borderRadius: '6px',
                marginTop: '12px',
              }}
            >
              <strong>⚠️ Warning:</strong>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                {currentStatus === 'Completed' ? (
                  <>
                    <li>
                      This will remove volunteer points previously awarded
                    </li>
                    <li>The signup will return to incomplete status</li>
                  </>
                ) : (
                  <li>The signup will return to incomplete status</li>
                )}
              </ul>
            </div>
          </div>
        ),
        confirmLabel: 'Mark Incomplete',
        confirmClassName: styles.confirmIncomplete,
      },
      delete: {
        // Cancelling is a status change, not a delete: the signup is kept in
        // history as Cancelled and the spot is freed (the API's "delete"
        // action sets Status = Cancelled — volunteer-role-deletion-design.md §7).
        title: 'Cancel Signup',
        content: (
          <div>
            <p>
              Cancel <strong>{memberName}</strong>&apos;s signup for{' '}
              <strong>{roleName}</strong>?
            </p>
            <div
              style={{
                background: '#fff3cd',
                padding: '12px',
                borderRadius: '6px',
                marginTop: '12px',
              }}
            >
              <strong>⚠️ This will:</strong>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Cancel the signup and free the spot for another volunteer</li>
                {currentStatus === 'Completed' && (
                  <li>Remove the volunteer points previously awarded</li>
                )}
                <li>{memberName} can sign up again while spots remain</li>
              </ul>
            </div>
          </div>
        ),
        confirmLabel: 'Cancel Signup',
        cancelLabel: 'Keep Signup',
        confirmClassName: styles.confirmDelete,
      },
    };

    return confirmations[newAction] || null;
  };

  const showConfirmation = (
    signupId,
    currentStatus,
    newAction,
    memberName,
    roleName
  ) => {
    const confirmDetails = getConfirmationDetails(
      currentStatus,
      newAction,
      memberName,
      roleName
    );
    if (!confirmDetails) return;

    setConfirmModal({
      show: true,
      ...confirmDetails,
      onConfirm: () => executeAction(signupId, newAction),
    });
  };

  const hideConfirmation = () => {
    setConfirmModal({
      show: false,
      title: '',
      content: '',
      confirmLabel: '',
      onConfirm: () => {},
      confirmClassName: '',
    });
  };

  const executeAction = async (signupId, action) => {
    setBulkActionLoading(true);
    try {
      await manageSignups({ actions: [{ signupId, action }] });

      // Refresh data
      const { data: refreshed } = await getAllMeetSignups(meetId);
      setData(refreshed);

      const actionText = {
        complete: 'completed and points awarded',
        no_show: 'marked as no-show',
        registered: 'reset to incomplete',
        delete: 'cancelled and removed',
      }[action];

      toast.success(`Volunteer signup ${actionText} successfully!`);
      hideConfirmation();
    } catch (err) {
      toast.error(err.message || 'Failed to update volunteer status.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Get the appropriate buttons for each signup status
  const getStatusButtons = (signup) => {
    const { status, volunteerSignupID, userName } = signup;
    const role = data.roles.find((r) =>
      r.signups.some((s) => s.volunteerSignupID === volunteerSignupID)
    );
    const roleName = formatRoleName(role) || 'Unknown Role';

    const buttons = [];

    switch (status) {
      case 'Registered':
      case 'Confirmed':
        buttons.push(
          <button
            key="complete"
            onClick={() =>
              showConfirmation(
                volunteerSignupID,
                status,
                'complete',
                userName,
                roleName
              )
            }
            disabled={bulkActionLoading}
            className={styles.completeButton}
            title="Mark volunteer assignment as completed and award points"
          >
            Complete
          </button>,
          <button
            key="no_show"
            onClick={() =>
              showConfirmation(
                volunteerSignupID,
                status,
                'no_show',
                userName,
                roleName
              )
            }
            disabled={bulkActionLoading}
            className={styles.noShowButton}
            title="Mark volunteer as no-show (no points awarded)"
          >
            No-Show
          </button>
        );
        break;

      case 'Completed':
        buttons.push(
          <button
            key="incomplete"
            onClick={() =>
              showConfirmation(
                volunteerSignupID,
                status,
                'registered',
                userName,
                roleName
              )
            }
            disabled={bulkActionLoading}
            className={styles.incompleteButton}
            title="Reset to incomplete status (removes points)"
          >
            Incomplete
          </button>,
          <button
            key="no_show"
            onClick={() =>
              showConfirmation(
                volunteerSignupID,
                status,
                'no_show',
                userName,
                roleName
              )
            }
            disabled={bulkActionLoading}
            className={styles.noShowButton}
            title="Mark volunteer as no-show (removes points)"
          >
            No-Show
          </button>
        );
        break;

      case 'NoShow':
        buttons.push(
          <button
            key="complete"
            onClick={() =>
              showConfirmation(
                volunteerSignupID,
                status,
                'complete',
                userName,
                roleName
              )
            }
            disabled={bulkActionLoading}
            className={styles.completeButton}
            title="Mark volunteer assignment as completed and award points"
          >
            Complete
          </button>,
          <button
            key="incomplete"
            onClick={() =>
              showConfirmation(
                volunteerSignupID,
                status,
                'registered',
                userName,
                roleName
              )
            }
            disabled={bulkActionLoading}
            className={styles.incompleteButton}
            title="Reset to incomplete status"
          >
            Incomplete
          </button>
        );
        break;

      default:
        buttons.push(
          <button
            key="complete"
            onClick={() =>
              showConfirmation(
                volunteerSignupID,
                status,
                'complete',
                userName,
                roleName
              )
            }
            disabled={bulkActionLoading}
            className={styles.completeButton}
            title="Mark volunteer assignment as completed and award points"
          >
            Complete
          </button>,
          <button
            key="no_show"
            onClick={() =>
              showConfirmation(
                volunteerSignupID,
                status,
                'no_show',
                userName,
                roleName
              )
            }
            disabled={bulkActionLoading}
            className={styles.noShowButton}
            title="Mark volunteer as no-show (no points awarded)"
          >
            No-Show
          </button>
        );
        break;
    }

    // Always add cancel button
    buttons.push(
      <button
        key="cancel"
        onClick={() =>
          showConfirmation(
            volunteerSignupID,
            status,
            'delete',
            userName,
            roleName
          )
        }
        disabled={bulkActionLoading}
        className={styles.cancelButton}
        title="Remove this volunteer signup"
      >
        Cancel
      </button>
    );

    return buttons;
  };

  const handleBulkSignup = async () => {
    if (!selectedRole || selectedMembers.length === 0) {
      toast.error('Please select a role and at least one member.');
      return;
    }

    setBulkActionLoading(true);
    try {
      const response = await bulkSignup({
        meetVolunteerRequirementID: selectedRole,
        userIDs: selectedMembers.map((m) => m.userID),
        notes: '',
      });

      // Refresh data
      const { data: refreshed } = await getAllMeetSignups(meetId);
      setData(refreshed);

      toast.success(
        `Successfully signed up ${response.data.successfulSignups} of ${response.data.totalRequested} members!`
      );

      // Reset form
      setSelectedMembers([]);
      setSelectedRole('');
      //setShowBulkSignup(false);  // Uncomment to hide bulk signup after submission
    } catch (err) {
      toast.error(err.message || 'Failed to sign up members.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <Loading />
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.container}>
        <div className={styles.errorMessage}>No volunteer data available</div>
      </div>
    );
  }

  const { general, eventSpecific, byAgeGroup, byAthleteGroup } =
    getGroupedAndSortedRoles(data.roles);
  const filteredRoles = getFilteredRoles();
  const filteredMembers = getFilteredMembers();
  const uniqueRoles = getUniqueRoles();
  const uniqueAgeGroups = getUniqueAgeGroups();

  return (
    <div className={styles.container}>
      {/* Confirmation Modal */}
      <CustomConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        content={confirmModal.content}
        confirmLabel={confirmModal.confirmLabel}
        cancelLabel={confirmModal.cancelLabel}
        onConfirm={confirmModal.onConfirm}
        onCancel={hideConfirmation}
        confirmClassName={confirmModal.confirmClassName}
        loading={bulkActionLoading}
      />

      <MeetPageHeader
        title="Volunteer Management"
        meetName={data.meetName}
        meetDate={convertDateTime(data.meetDate).date}
        onBackToMeet={() => navigate(routes.admin.showMeet.url(clubId, meetId))}
        titleBadge={
          <span className={styles.volunteerCountBadge}>
            {data.totalVolunteersSignedUp}/{data.totalVolunteersNeeded}{' '}
            volunteers
          </span>
        }
      />

      {/* Bulk Signup Section */}
      <div className={styles.bulkSignupSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>➕</span>
          <span>Sign Up Members</span>
          <button
            className={styles.toggleButton}
            onClick={() => setShowBulkSignup(!showBulkSignup)}
          >
            {showBulkSignup ? 'Hide' : 'Show'}
          </button>
        </div>

        {showBulkSignup && (
          <div className={styles.bulkSignupControls}>
            <div className={styles.bulkSignupCard}>
              <div className={styles.formSectionNew}>
                {/* Column 1: Role Selection with Age Group Filter */}
                <div className={styles.roleSelectionColumn}>
                  <label className={styles.formLabel}>
                    1. Select a Volunteer Role
                  </label>

                  {/* Age Group Filter Dropdown */}
                  <select
                    value={ageGroupFilter}
                    onChange={(e) => setAgeGroupFilter(e.target.value)}
                    className={styles.ageGroupFilterSelect}
                  >
                    {uniqueAgeGroups.map((ageGroup) => (
                      <option key={ageGroup} value={ageGroup}>
                        {/* 'All age groups' is the filter's own value for "no filter"; it is shown in the club's words. */}
                        {ageGroup === 'All age groups' ? `All ${words.lower.groups}` : ageGroup}
                      </option>
                    ))}
                  </select>

                  {/* Hide Fully Staffed Checkbox */}
                  <label className={styles.hideFullyStaffedLabel}>
                    <input
                      type="checkbox"
                      checked={hideFullyStaffed}
                      onChange={(e) => setHideFullyStaffed(e.target.checked)}
                      className={styles.hideFullyStaffedCheckbox}
                    />
                    Hide fully staffed roles
                  </label>

                  <select
                    ref={roleSelectRef}
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className={styles.selectInput}
                    size="10"
                  >
                    {filteredRoles.map((role) => (
                      <option
                        key={role.meetVolunteerRequirementID}
                        value={role.meetVolunteerRequirementID}
                      >
                        {formatRoleName(role)} ({role.numberStillNeeded} needed)
                      </option>
                    ))}
                  </select>
                </div>

                {/* Column 2: Member Search and Available Members */}
                <div className={styles.memberSearchColumn}>
                  <label className={styles.formLabel}>
                    2. Select Members
                    <span className={styles.selectionCount}>
                      {(() => {
                        const status = getSelectionStatus();
                        return status ? (
                          <span
                            className={`${styles.selectionStatus} ${styles[status.type]}`}
                          >
                            {status.message}
                          </span>
                        ) : null;
                      })()}
                    </span>
                  </label>
                  <div className={styles.memberSearchContainer}>
                    <div className={styles.memberSearchControls}>
                      <div className={styles.searchInputWrapper}>
                        <input
                          type="text"
                          placeholder="Search members..."
                          value={memberSearchTerm}
                          onChange={(e) => setMemberSearchTerm(e.target.value)}
                          className={styles.memberSearchInput}
                        />
                        {memberSearchTerm && (
                          <button
                            className={styles.clearSearchButton}
                            onClick={() => setMemberSearchTerm('')}
                            type="button"
                            aria-label="Clear search"
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <select
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        className={styles.roleFilterSelect}
                      >
                        {uniqueRoles.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.availableMembersList}>
                      {!selectedRole ? (
                        <div className={styles.memberListHint}>
                          Select a volunteer role first
                        </div>
                      ) : getAvailableSlots() === 0 ? (
                        <div className={styles.memberListHint}>
                          Role is fully staffed
                        </div>
                      ) : filteredMembers.length === 0 ? (
                        <div className={styles.memberListHint}>
                          No members found matching filters
                        </div>
                      ) : (
                        filteredMembers.map((member) => {
                          const signupCount = getUserSignupCount(member.userID);
                          const isAlreadySignedUp = selectedRole
                            ? data.roles
                                .find(
                                  (r) =>
                                    r.meetVolunteerRequirementID ===
                                    selectedRole
                                )
                                ?.signups.some(
                                  (s) => s.userID === member.userID
                                )
                            : false;
                          const isAlreadySelected = selectedMembers.some(
                            (m) => m.userID === member.userID
                          );

                          return (
                            <div
                              key={member.userID}
                              className={`${styles.availableMemberItem} ${
                                isAlreadySignedUp || isAlreadySelected
                                  ? styles.disabledMember
                                  : ''
                              }`}
                              onClick={() =>
                                !isAlreadySignedUp &&
                                !isAlreadySelected &&
                                handleAddMember(member.userID)
                              }
                            >
                              <span className={styles.memberName}>
                                {getMemberDisplayName(member)}
                                {member.role === 'Admin' && (
                                  <span className={styles.adminBadge}>
                                    Admin
                                  </span>
                                )}
                                {signupCount > 0 && (
                                  <span className={styles.roleCount}>
                                    ({signupCount} roles)
                                  </span>
                                )}
                              </span>
                              {isAlreadySignedUp && (
                                <span className={styles.alreadySignedUp}>
                                  Already signed up
                                </span>
                              )}
                              {isAlreadySelected && (
                                <span className={styles.alreadySelected}>
                                  Selected
                                </span>
                              )}
                              {!isAlreadySignedUp && !isAlreadySelected && (
                                <button className={styles.addMemberButton}>
                                  +
                                </button>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>

                {/* Column 3: Selected Members List */}
                <div className={styles.selectedMembersColumn}>
                  <label className={styles.formLabel}>
                    3. Confirm Sign Ups ({selectedMembers.length})
                    {selectedMembers.length > 0 && (
                      <button
                        className={styles.clearAllButton}
                        onClick={handleClearAll}
                        type="button"
                      >
                        Clear All
                      </button>
                    )}
                  </label>
                  <div className={styles.selectedMembersWrapper}>
                    <div className={styles.selectedMembersList}>
                      {selectedMembers.length === 0 ? (
                        <div className={styles.noMembersSelected}>
                          No members selected yet
                        </div>
                      ) : (
                        selectedMembers.map((member) => (
                          <div
                            key={member.userID}
                            className={styles.selectedMemberItem}
                          >
                            <span className={styles.selectedMemberName}>
                              {getMemberDisplayName(member)}
                            </span>
                            <button
                              className={styles.removeMemberButton}
                              onClick={() => handleRemoveMember(member.userID)}
                              aria-label={`Remove ${getMemberDisplayName(member)}`}
                            >
                              ×
                            </button>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Confirm Button at bottom */}
                    <button
                      onClick={handleBulkSignup}
                      disabled={
                        !selectedRole ||
                        selectedMembers.length === 0 ||
                        bulkActionLoading
                      }
                      className={styles.primarySignupButton}
                    >
                      {bulkActionLoading ? (
                        <>
                          <span className={styles.spinner}></span>
                          Signing Up...
                        </>
                      ) : (
                        <>
                          ➕ Sign Up {selectedMembers.length || 0} Member
                          {selectedMembers.length === 1 ? '' : 's'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Roles Management - Updated with proper age group sections */}
      <div className={styles.rolesSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>👥</span>
          <span>Manage Sign Up Status</span>
        </div>

        {/* General Roles */}
        {general.filter((r) => r.signups.length > 0).length > 0 && (
          <div className={styles.roleCategory}>
            <div className={styles.roleCategoryTitle}>General</div>
            {general
              .filter((role) => role.signups.length > 0)
              .map((role) => (
                <div
                  key={role.meetVolunteerRequirementID}
                  className={styles.roleSection}
                >
                  <div className={styles.roleHeader}>
                    <span className={styles.roleName}>{role.roleName}</span>
                  </div>
                  <div className={styles.volunteersList}>
                    {role.signups.map((signup) => {
                      const memberSignupCount = getUserSignupCount(
                        signup.userID
                      );
                      return (
                        <div
                          key={signup.volunteerSignupID}
                          className={styles.volunteerRow}
                        >
                          <div className={styles.volunteerInfo}>
                            <span className={styles.volunteerName}>
                              {signup.userName}
                              {signup.isCurrentUser && ' (You)'}
                              {memberSignupCount > 1 && (
                                <span
                                  className={styles.multiRoleBadge}
                                  title={`Signed up for ${memberSignupCount} roles`}
                                >
                                  {memberSignupCount} roles
                                </span>
                              )}
                            </span>
                          </div>
                          <div className={styles.statusSection}>
                            <span
                              className={`${styles.statusBadge} ${styles[signup.status.toLowerCase()]}`}
                            >
                              {signup.status}
                            </span>
                          </div>
                          <div className={styles.actionsSection}>
                            {getStatusButtons(signup)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Event-Specific Roles */}
        {eventSpecific.filter((r) => r.signups.length > 0).length > 0 && (
          <div className={styles.roleCategory}>
            <div className={styles.roleCategoryTitle}>Event Officials</div>
            {eventSpecific
              .filter((role) => role.signups.length > 0)
              .map((role) => (
                <div
                  key={role.meetVolunteerRequirementID}
                  className={styles.roleSection}
                >
                  <div className={styles.roleHeader}>
                    <span className={styles.roleName}>{role.roleName}</span>
                  </div>
                  <div className={styles.volunteersList}>
                    {role.signups.map((signup) => {
                      const memberSignupCount = getUserSignupCount(
                        signup.userID
                      );
                      return (
                        <div
                          key={signup.volunteerSignupID}
                          className={styles.volunteerRow}
                        >
                          <div className={styles.volunteerInfo}>
                            <span className={styles.volunteerName}>
                              {signup.userName}
                              {signup.isCurrentUser && ' (You)'}
                              {memberSignupCount > 1 && (
                                <span
                                  className={styles.multiRoleBadge}
                                  title={`Signed up for ${memberSignupCount} roles`}
                                >
                                  {memberSignupCount} roles
                                </span>
                              )}
                            </span>
                          </div>
                          <div className={styles.statusSection}>
                            <span
                              className={`${styles.statusBadge} ${styles[signup.status.toLowerCase()]}`}
                            >
                              {signup.status}
                            </span>
                          </div>
                          <div className={styles.actionsSection}>
                            {getStatusButtons(signup)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Athlete Group Specific Roles */}
        {Object.values(byAthleteGroup)
          .sort((a, b) => {
            const aOrder = groupOrderOfName(a.ageGroupCode);
            const bOrder = groupOrderOfName(b.ageGroupCode);
            if (aOrder !== bOrder) return aOrder - bOrder;
            return a.groupName.localeCompare(b.groupName);
          })
          .map((group) => {
            const rolesWithSignups = group.roles.filter(
              (r) => r.signups.length > 0
            );
            if (rolesWithSignups.length === 0) return null;

            return (
              <div key={group.groupName} className={styles.roleCategory}>
                <div className={styles.roleCategoryTitle}>
                  {group.groupName}
                  <Users size={16} className={styles.groupIcon} />
                </div>
                {rolesWithSignups.map((role) => (
                  <div
                    key={role.meetVolunteerRequirementID}
                    className={styles.roleSection}
                  >
                    <div className={styles.roleHeader}>
                      <span className={styles.roleName}>{role.roleName}</span>
                    </div>
                    <div className={styles.volunteersList}>
                      {role.signups.map((signup) => {
                        const memberSignupCount = getUserSignupCount(
                          signup.userID
                        );
                        return (
                          <div
                            key={signup.volunteerSignupID}
                            className={styles.volunteerRow}
                          >
                            <div className={styles.volunteerInfo}>
                              <span className={styles.volunteerName}>
                                {signup.userName}
                                {signup.isCurrentUser && ' (You)'}
                                {memberSignupCount > 1 && (
                                  <span
                                    className={styles.multiRoleBadge}
                                    title={`Signed up for ${memberSignupCount} roles`}
                                  >
                                    {memberSignupCount} roles
                                  </span>
                                )}
                              </span>
                            </div>
                            <div className={styles.statusSection}>
                              <span
                                className={`${styles.statusBadge} ${styles[signup.status.toLowerCase()]}`}
                              >
                                {signup.status}
                              </span>
                            </div>
                            <div className={styles.actionsSection}>
                              {getStatusButtons(signup)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}

        {/* Age Group Specific Roles */}
        {Object.values(byAgeGroup)
          .sort(
            (a, b) =>
              groupOrderOfName(a.ageGroupCode) -
              groupOrderOfName(b.ageGroupCode)
          )
          .map((group) => {
            const rolesWithSignups = group.roles.filter(
              (r) => r.signups.length > 0
            );
            if (rolesWithSignups.length === 0) return null;

            return (
              <div key={group.displayName} className={styles.roleCategory}>
                <div className={styles.roleCategoryTitle}>
                  {group.displayName} {words.group}
                </div>
                {rolesWithSignups.map((role) => (
                  <div
                    key={role.meetVolunteerRequirementID}
                    className={styles.roleSection}
                  >
                    <div className={styles.roleHeader}>
                      <span className={styles.roleName}>{role.roleName}</span>
                    </div>
                    <div className={styles.volunteersList}>
                      {role.signups.map((signup) => {
                        const memberSignupCount = getUserSignupCount(
                          signup.userID
                        );
                        return (
                          <div
                            key={signup.volunteerSignupID}
                            className={styles.volunteerRow}
                          >
                            <div className={styles.volunteerInfo}>
                              <span className={styles.volunteerName}>
                                {signup.userName}
                                {signup.isCurrentUser && ' (You)'}
                                {memberSignupCount > 1 && (
                                  <span
                                    className={styles.multiRoleBadge}
                                    title={`Signed up for ${memberSignupCount} roles`}
                                  >
                                    {memberSignupCount} roles
                                  </span>
                                )}
                              </span>
                            </div>
                            <div className={styles.statusSection}>
                              <span
                                className={`${styles.statusBadge} ${styles[signup.status.toLowerCase()]}`}
                              >
                                {signup.status}
                              </span>
                            </div>
                            <div className={styles.actionsSection}>
                              {getStatusButtons(signup)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
      </div>
    </div>
  );
};

VolunteerManagement.propTypes = {
  clubId: PropTypes.string.isRequired,
  meetId: PropTypes.string.isRequired,
};

export default VolunteerManagement;
